import { ArrowRight, CalendarCheck, MapPin, Users } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { formatDateTime } from "../../lib/format";
import { Card, CardHeader, EmptyState, StatusPill } from "../../ui/components";

// What the two halves of the building have in common when somebody opens the product: one thing
// happening next, where it is, who it is for, and a way in. A hire and one of the client's own
// events are different records with different fields, so each home maps its own answer into this
// shape and these sections never reach into two APIs to find out which they are holding.
export interface HomeRecord {
  id: string;
  reference: string;
  title: string;
  status: string;
  start: string;
  timezone: string;
  summary: string;
  customerName: string;
  attendeeCount?: number | undefined;
  venueNames: string[];
  locationNames: string[];
  // Where this opens. The two kinds live on different routes, and following the wrong one lands on
  // a page that cannot exist.
  path: string;
}

export interface ReadinessTile {
  key: string;
  label: string;
  icon: ReactNode;
  value: ReactNode;
  attention?: boolean;
}

export interface NextUpLabels {
  eyebrow: string;
  heading: string;
  openAria: string;
  emptyTitle: string;
  emptyDescription: string;
  emptyActionLabel: string;
  emptyActionTo: string;
}

export function formatHomeDatePart(
  value: string,
  timezone: string,
  options: Intl.DateTimeFormatOptions,
): string {
  return new Intl.DateTimeFormat(undefined, { timeZone: timezone, ...options }).format(
    new Date(value),
  );
}

// A count that is honest about not knowing yet. Three states rather than a number and a spinner,
// because a tile reading zero while its request is still in the air is a wrong answer rather than
// an incomplete one, and somebody acts on it.
export function queryCount(loading: boolean, error: unknown, value: number): string {
  if (loading) return "Loading";
  if (error) return "Unavailable";
  return String(value);
}

// The greeting follows the viewer's own clock, not the venue's: a person reading
// "Good morning" at nine in the evening hears a machine rather than a welcome.
export function timeGreeting(now = new Date()): string {
  const hour = now.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

export function NextUpSection({
  record,
  labels,
}: {
  record: HomeRecord | undefined;
  labels: NextUpLabels;
}) {
  return (
    <section aria-labelledby="home-next-heading" className="home-section">
      <div className="section-heading-row">
        <div>
          <span className="eyebrow">{labels.eyebrow}</span>
          <h2 id="home-next-heading">{labels.heading}</h2>
        </div>
        <span className="muted">Your authorized venues only</span>
      </div>
      {record ? (
        <Link
          className="next-event-link"
          to={record.path}
          aria-label={`${labels.openAria} ${record.title}`}
        >
          <article className="next-event-card">
            <div className="event-date-tile" aria-hidden="true">
              <span>{formatHomeDatePart(record.start, record.timezone, { month: "short" })}</span>
              <strong>
                {formatHomeDatePart(record.start, record.timezone, { day: "numeric" })}
              </strong>
              <small>
                {formatHomeDatePart(record.start, record.timezone, { weekday: "short" })}
              </small>
            </div>
            <div className="next-event-main">
              <div className="split start">
                <div>
                  <span className="eyebrow">{record.reference}</span>
                  <h3>{record.title}</h3>
                </div>
                <StatusPill value={record.status} />
              </div>
              <p className="next-event-time">
                <CalendarCheck size={18} aria-hidden="true" />
                {formatDateTime(record.start, record.timezone)}
              </p>
              <div className="event-meta-grid">
                <span>
                  <MapPin size={17} aria-hidden="true" />
                  <span>
                    <small>Venue and space</small>
                    <strong>
                      {record.locationNames.length
                        ? `${record.venueNames.join(", ")} · ${record.locationNames.join(", ")}`
                        : "Location being finalized"}
                    </strong>
                  </span>
                </span>
                <span>
                  <Users size={17} aria-hidden="true" />
                  <span>
                    <small>Customer and guests</small>
                    <strong>
                      {record.customerName || "No customer named"} ·{" "}
                      {record.attendeeCount ?? "Guest count pending"}
                    </strong>
                  </span>
                </span>
              </div>
              {record.summary ? <p className="event-summary">{record.summary}</p> : null}
            </div>
            <div className="event-open-cue">
              Open overview <ArrowRight size={18} aria-hidden="true" />
            </div>
          </article>
        </Link>
      ) : (
        <Card>
          <EmptyState
            title={labels.emptyTitle}
            description={labels.emptyDescription}
            action={
              <Link className="button primary" to={labels.emptyActionTo}>
                {labels.emptyActionLabel}
              </Link>
            }
          />
        </Card>
      )}
    </section>
  );
}

export function ReadinessSection({
  eyebrow,
  heading,
  briefTo,
  briefLabel,
  tiles,
}: {
  eyebrow: string;
  heading: string;
  briefTo: string;
  briefLabel: string;
  tiles: ReadinessTile[];
}) {
  return (
    <section aria-labelledby="home-readiness-heading" className="home-section">
      <div className="section-heading-row">
        <div>
          <span className="eyebrow">{eyebrow}</span>
          <h2 id="home-readiness-heading">{heading}</h2>
        </div>
        <Link to={briefTo}>{briefLabel}</Link>
      </div>
      <div className="readiness-grid">
        {tiles.map((tile) => (
          <div className={`readiness-item ${tile.attention ? "attention" : ""}`} key={tile.key}>
            {tile.icon}
            <span>
              <small>{tile.label}</small>
              <strong>{tile.value}</strong>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

export function UpcomingSection({
  records,
  title,
  description,
  emptyTitle,
  emptyDescription,
}: {
  records: HomeRecord[];
  title: string;
  description: string;
  emptyTitle: string;
  emptyDescription: string;
}) {
  return (
    <section aria-label={title} className="home-section">
      <Card>
        <CardHeader
          title={title}
          description={description}
          actions={<Link to="/calendar">View full calendar</Link>}
        />
        {records.length ? (
          <div className="upcoming-event-list">
            {records.map((record) => (
              <Link className="upcoming-event" to={record.path} key={record.id}>
                <span className="upcoming-event-date">
                  <strong>
                    {formatHomeDatePart(record.start, record.timezone, { day: "numeric" })}
                  </strong>
                  <small>
                    {formatHomeDatePart(record.start, record.timezone, { month: "short" })}
                  </small>
                </span>
                <span className="upcoming-event-title">
                  <strong>{record.title}</strong>
                  <small>{record.customerName}</small>
                </span>
                <span className="upcoming-event-place">
                  {record.venueNames.join(", ") || "Venue pending"}
                </span>
                <StatusPill value={record.status} />
                <ArrowRight size={17} aria-hidden="true" />
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState title={emptyTitle} description={emptyDescription} />
        )}
      </Card>
    </section>
  );
}
